// 爆款雷達 — which new listings will become hits, and which likely hits have no Meta ad yet.
// Owner-only, at /owner-board/radar. Reads the daily snapshot written by the workspace script
// scripts/oms/hit_radar.py (run by the 每日檢查 conversation). Hit odds are calibrated on
// Metanoia's own complete listings (hit = ≥10 storefront units in 30 days). CBO decides ad
// spend, so the page never says "push budget": it flags likely hits that are missing from
// Meta, and Magnus can tick them and start /video-ad-pipeline-v2 on exactly those SKUs.
// 找商品 (Magnus 2026-10-18): search EVERY active product, see which have no ad yet and which
// already have a Seedance video on the PC, and send any no-ad product to the same video run.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, InputAdornment, Paper, Stack, TextField, Typography } from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import MovieCreationOutlinedIcon from '@mui/icons-material/MovieCreationOutlined';
import api from '../services/api';
import { errMessage } from '../components/agent/agentShared';

interface RadarItem {
  sid: string;
  sku: string | null;
  title: string;
  ptype: string;
  price: number;
  handle: string | null;
  img: string | null;
  age: number;
  units: number;
  revenue: number;
  p: number;
  curve: number[];
  in_meta: boolean | null;
  ads_active: number;
  ads_paused: number;
}
interface Group {
  k: string;
  n: number;
  hits: number;
  rate: number;
  rev_per_listing: number;
}
// One active product in the search catalog (hit_radar.py build_catalog — compact keys).
interface CatalogItem {
  sku: string | null;
  t: string;
  pt: string;
  pr: number;
  img: string | null;
  d: string; // listed on (YYYY-MM-DD)
  u30: number; // storefront units, last 30 days
  ads: number | null; // Meta ads with this SKU, any status (null = Meta not read)
  act: number; // of which ACTIVE
  videos: number; // usable Seedance videos on the PC
  vu: number; // of which never used for an ad
  c30?: number[] | null; // daily storefront units, last 30 days oldest → today (null = none sold)
}
interface RadarData {
  generated_at: string;
  measured_day: string;
  n_complete: number;
  n_hits: number;
  base: number;
  hit_rev_share: number;
  day7: Record<string, [number, number]>;
  meta_checked: boolean;
  likely_count: number;
  missing_count: number;
  current: RadarItem[];
  types: Group[];
  bands: Group[];
  dead_count: number;
  dead: { sid: string; sku: string | null; title: string; img: string | null; price: number; age: number }[];
  catalog?: CatalogItem[];
}

const LIKELY = 0.3;
const MAX_PICK = 8; // POST /radar/video-run takes at most 8 SKUs
const PAGE = 24;
const HOT = '#e2462f';
const EMPTY30: number[] = Array(30).fill(0);
const BUCKETS = ['0', '1', '2', '3-4', '5-9', '10+'];
const BUCKET_LABEL: Record<string, string> = { '0': '0 件', '1': '1 件', '2': '2 件', '3-4': '3–4 件', '5-9': '5–9 件', '10+': '10 件以上' };
const TYPE_ZH: Record<string, string> = {
  shorts: '短褲', pants: '長褲', tshirt: 'T 恤', shirt: '襯衫', knit: '針織', vest: '背心', accessory: '配件', jacket: '外套',
};
const pct = (x: number) => `${Math.round(x * 100)}%`;
const shortTitle = (t: string) => t.replace(/^【Metanoia】\s*/, '').replace('🇰🇷韓製', '').replace('韓國設計', '').trim();

// Daily units since listing; days 1–7 in the accent, a dashed line at day 7.
// `recent`: a trailing window (oldest → today) instead — the LAST 7 days get the accent.
const Spark: React.FC<{ curve: number[]; recent?: boolean }> = ({ curve, recent }) => {
  const w = 132;
  const h = 30;
  const mx = Math.max(1, ...curve);
  // Fixed 14-day width so a 2-day-old listing doesn't draw two giant bars.
  const step = w / Math.max(curve.length, 14);
  const cut = recent ? curve.length - 7 : 7;
  const accent = (i: number) => (recent ? i >= cut : i < cut);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 30, display: 'block' }} aria-hidden="true">
      {curve.length > 7 && <line x1={cut * step} y1={0} x2={cut * step} y2={h} stroke="#9aa1ad" strokeDasharray="2 3" />}
      {curve.map((v, i) => {
        const bh = v === 0 ? 1 : Math.max(3, ((h - 3) * v) / mx);
        return (
          <rect
            key={i}
            x={i * step + 1}
            y={h - bh}
            width={Math.max(2, step - 2)}
            height={bh}
            rx={1}
            fill={v === 0 ? '#dcdcd8' : accent(i) ? HOT : '#8a909b'}
          />
        );
      })}
    </svg>
  );
};

const HitRadarPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<RadarData | null>(null);
  const [day, setDay] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [showRest, setShowRest] = useState(false);
  const [starting, setStarting] = useState(false);
  const [q, setQ] = useState('');
  const [onlyNoAd, setOnlyNoAd] = useState(true);
  const [onlyVideo, setOnlyVideo] = useState(false);
  const [sortBy, setSortBy] = useState<'sold' | 'new'>('sold');
  const [onlyRecent, setOnlyRecent] = useState(false); // listed in the last 30 days
  // 下架候選 ticked for delisting (sid → item), and which ones already went to a 下架 run.
  const [delist, setDelist] = useState<Record<string, { sid: string; sku: string | null; title: string }>>({});
  const [delistSent, setDelistSent] = useState<Set<string>>(new Set());
  const [delisting, setDelisting] = useState(false);
  const [shown, setShown] = useState(PAGE);

  // SKUs already sent to the video pipeline (their 影片廣告 v2 card on the board) → run state.
  const [sent, setSent] = useState<Record<string, { thread: number | null; status: string | null }>>({});

  useEffect(() => {
    api
      .get('/api/owner-board/radar', { params: { catalog: 1 } })
      .then((res) => {
        setData(res.data.snapshot?.data || null);
        setDay(res.data.snapshot?.day || null);
      })
      .catch((err) => setError(errMessage(err)))
      .finally(() => setLoading(false));
    api
      .get('/api/owner-board')
      .then((res) => {
        const map: Record<string, { thread: number | null; status: string | null }> = {};
        const dl = new Set<string>();
        for (const it of res.data.items || []) {
          if (String(it.title || '').startsWith('下架：') && it.status !== 'done') {
            for (const s of `${it.detail || ''}`.match(/\b\d{3,20}\b/g) || []) dl.add(s);
          }
          if (!String(it.title || '').startsWith('影片廣告 v2')) continue;
          const skus = `${it.title} ${it.detail || ''}`.match(/\b\d{4}\b/g) || [];
          for (const s of skus) map[s] = { thread: it.thread_id, status: it.thread_status };
        }
        setSent(map);
        setDelistSent(dl);
      })
      .catch(() => setSent({}));
  }, []);

  const SENT_LABEL: Record<string, string> = {
    queued: '已送影片管線 · 排隊中',
    running: '已送影片管線 · 製作中',
    awaiting_approval: '已送影片管線 · 等你核准',
    idle: '影片廣告已做好（PAUSED）· 看結果',
    failed: '影片沒跑完 · 看對話',
  };

  const { missing, covered, rest } = useMemo(() => {
    const cur = data?.current || [];
    return {
      missing: cur.filter((r) => r.p >= LIKELY && r.in_meta === false),
      covered: cur.filter((r) => r.p >= LIKELY && r.in_meta !== false),
      rest: cur.filter((r) => r.p < LIKELY),
    };
  }, [data]);

  const toggle = (sku: string | null) => {
    if (!sku) return;
    if (!picked.has(sku) && picked.size >= MAX_PICK) {
      setError(`一次最多 ${MAX_PICK} 款`);
      return;
    }
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  };

  const catalog = useMemo(() => data?.catalog || [], [data]);
  const bySku = useMemo(() => {
    const m: Record<string, CatalogItem> = {};
    for (const c of catalog) if (c.sku) m[c.sku] = c;
    return m;
  }, [catalog]);
  const found = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const since = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
    const rows = catalog.filter(
      (c) =>
        (!onlyNoAd || c.ads === 0) &&
        (!onlyRecent || c.d >= since) &&
        (!onlyVideo || c.videos > 0) &&
        words.every((w) => (c.sku || '').startsWith(w) || c.t.toLowerCase().includes(w) || (TYPE_ZH[c.pt] || c.pt).includes(w))
    );
    return sortBy === 'sold' ? [...rows].sort((a, b) => b.u30 - a.u30 || b.d.localeCompare(a.d)) : rows;
  }, [catalog, q, onlyNoAd, onlyVideo, onlyRecent, sortBy]);
  const pickedWithVideo = Array.from(picked).filter((s) => (bySku[s]?.videos || 0) > 0);

  const toggleDelist = (d: { sid: string; sku: string | null; title: string }) => {
    setDelist((prev) => {
      const next = { ...prev };
      if (next[d.sid]) delete next[d.sid];
      else if (Object.keys(next).length < 30) next[d.sid] = { sid: d.sid, sku: d.sku, title: shortTitle(d.title) };
      else setError('一次最多下架 30 款');
      return next;
    });
  };

  const startDelist = async () => {
    const items = Object.values(delist);
    if (!window.confirm(`確定下架這 ${items.length} 款？官網改成 archived、蝦皮有上架的也下架（NOKE 或預備貨有庫存的會跳過）。`)) return;
    setDelisting(true);
    try {
      const res = await api.post('/api/owner-board/radar/delist', { items });
      navigate(`/owner-board/t/${res.data.thread_id}`);
    } catch (err) {
      setError(errMessage(err));
      setDelisting(false);
    }
  };

  const startVideos = async () => {
    setStarting(true);
    try {
      const res = await api.post('/api/owner-board/radar/video-run', { skus: Array.from(picked) });
      navigate(`/owner-board/t/${res.data.thread_id}`);
    } catch (err) {
      setError(errMessage(err));
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const card = (r: RadarItem, selectableIfFree: boolean) => {
    const s = r.sku ? sent[r.sku] : undefined;
    const selectable = selectableIfFree && !s;
    const on = !!r.sku && picked.has(r.sku);
    return (
      <Paper
        key={r.sid}
        variant="outlined"
        onClick={selectable ? () => toggle(r.sku) : undefined}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          cursor: selectable ? 'pointer' : 'default',
          borderColor: selectable ? (on ? HOT : '#f3b3a7') : 'divider',
          boxShadow: on ? `0 0 0 2px ${HOT} inset` : 'none',
          minWidth: 0,
        }}
      >
        <Box sx={{ position: 'relative', aspectRatio: '3 / 4', bgcolor: 'action.hover', maxWidth: '100%' }}>
          {r.img && (
            <Box component="img" src={r.img} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          )}
          <Chip
            size="small"
            label={pct(r.p)}
            sx={{
              position: 'absolute',
              left: 8,
              top: 8,
              fontWeight: 700,
              bgcolor: r.p >= 0.6 ? HOT : '#fbf0d7',
              color: r.p >= 0.6 ? '#fff' : '#9a6a08',
            }}
          />
          {selectable && (
            <Checkbox
              checked={on}
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggle(r.sku)}
              sx={{ position: 'absolute', right: 2, top: 2, bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1, p: 0.5 }}
            />
          )}
        </Box>
        <Box sx={{ p: 1.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.4, wordBreak: 'break-word' }}>
            {shortTitle(r.title)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>
            SKU {r.sku || '—'} · 第 {r.age} 天 · 賣 {r.units} 件 · NT${r.revenue.toLocaleString()}
          </Typography>
          <Spark curve={r.curve} />
          {s ? (
            <Typography
              variant="caption"
              onClick={() => s.thread && navigate(`/owner-board/t/${s.thread}`)}
              sx={{ display: 'block', mt: 0.5, fontWeight: 700, color: s.status === 'failed' ? 'error.main' : 'success.main', cursor: 'pointer' }}
            >
              {SENT_LABEL[s.status || ''] || '已送影片管線'} →
            </Typography>
          ) : (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 600, color: r.in_meta === false ? HOT : 'text.secondary' }}>
            {r.in_meta === false
              ? '還沒上 Meta'
              : r.in_meta === null
              ? 'Meta 狀態未知'
              : `Meta ${r.ads_active} 支在跑${r.ads_paused ? `、${r.ads_paused} 支暫停` : ''} · CBO 決定`}
          </Typography>
          )}
        </Box>
      </Paper>
    );
  };

  const productCard = (c: CatalogItem) => {
    const s = c.sku ? sent[c.sku] : undefined;
    const selectable = !!c.sku && c.ads === 0 && !s;
    const on = !!c.sku && picked.has(c.sku);
    return (
      <Paper
        key={`${c.sku}-${c.d}-${c.t}`}
        variant="outlined"
        onClick={selectable ? () => toggle(c.sku) : undefined}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          cursor: selectable ? 'pointer' : 'default',
          borderColor: on ? HOT : 'divider',
          boxShadow: on ? `0 0 0 2px ${HOT} inset` : 'none',
          minWidth: 0,
        }}
      >
        <Box sx={{ position: 'relative', aspectRatio: '3 / 4', bgcolor: 'action.hover' }}>
          {c.img && <Box component="img" src={c.img} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
          {c.videos > 0 && (
            <Chip
              size="small"
              icon={<MovieCreationOutlinedIcon sx={{ fontSize: 16 }} />}
              label={c.vu ? `已有影片 ${c.videos}` : `影片 ${c.videos}（已上過廣告）`}
              sx={{ position: 'absolute', left: 8, top: 8, maxWidth: 'calc(100% - 52px)', fontWeight: 700, bgcolor: '#1f2a44', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }}
            />
          )}
          {selectable && (
            <Checkbox
              checked={on}
              onClick={(e) => e.stopPropagation()}
              onChange={() => toggle(c.sku)}
              sx={{ position: 'absolute', right: 2, top: 2, bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1, p: 0.5 }}
            />
          )}
        </Box>
        <Box sx={{ p: 1.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.4, wordBreak: 'break-word' }}>
            {c.t}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>
            SKU {c.sku || '—'} · {c.d.slice(5)} 上架 · 30 天 {c.u30} 件 · NT${Math.round(c.pr).toLocaleString()}
          </Typography>
          {c.c30 !== undefined && <Spark curve={c.c30 || EMPTY30} recent />}
          {s ? (
            <Typography
              variant="caption"
              onClick={() => s.thread && navigate(`/owner-board/t/${s.thread}`)}
              sx={{ display: 'block', mt: 0.5, fontWeight: 700, color: s.status === 'failed' ? 'error.main' : 'success.main', cursor: 'pointer' }}
            >
              {SENT_LABEL[s.status || ''] || '已送影片管線'} →
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 600, color: c.ads === 0 ? HOT : 'text.secondary' }}>
              {c.ads === null ? 'Meta 狀態未知' : c.ads === 0 ? '還沒有廣告' : `已有廣告 ${c.ads} 支${c.act ? `（${c.act} 在跑）` : '（都暫停）'}`}
            </Typography>
          )}
          {!c.sku && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              沒有 SKU，不能送影片管線
            </Typography>
          )}
        </Box>
      </Paper>
    );
  };

  const grid = (children: React.ReactNode) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(auto-fill, minmax(200px, 1fr))' }, gap: 1.5, mt: 1.5 }}>
      {children}
    </Box>
  );

  const bars = (rows: Group[], label: (k: string) => string) => {
    const mx = Math.max(...rows.map((r) => r.rate), 0.0001);
    return rows.map((r) => (
      <Box key={r.k} sx={{ display: 'grid', gridTemplateColumns: '84px 1fr 44px', columnGap: 1, alignItems: 'center', py: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label(r.k)}
        </Typography>
        <Box sx={{ height: 12, bgcolor: 'action.hover', borderRadius: 1, overflow: 'hidden' }}>
          <Box sx={{ width: `${(100 * r.rate) / mx}%`, height: '100%', bgcolor: 'text.primary', borderRadius: 1 }} />
        </Box>
        <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {pct(r.rate)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ gridColumn: '2 / 4' }}>
          {r.hits}/{r.n} · 每款 30 天 NT${(r.rev_per_listing / 1000).toFixed(1)}K
        </Typography>
      </Box>
    ));
  };

  return (
    <Box sx={{ maxWidth: 1180, width: '100%', mx: 'auto', minWidth: 0, pb: picked.size || Object.keys(delist).length ? 10 : 2, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/owner-board')} sx={{ justifySelf: 'start', mb: 1 }}>
        每日看板
      </Button>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {!data ? (
        <Alert severity="info">還沒有雷達資料。每日檢查明天早上會跑 hit_radar.py，之後這裡每天更新。</Alert>
      ) : (
        <>
          {/* ── thesis + day-7 odds ── */}
          <Paper sx={{ p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '1fr 1fr' }, gap: 3, alignItems: 'center' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 1 }}>
                  爆款雷達 · 資料到 {day}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 900, mt: 0.5, lineHeight: 1.15 }}>
                  第 7 天定生死
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 520 }}>
                  用你自己 {data.n_complete} 款完整上架紀錄校正：上架第 7 天賣了幾件，幾乎決定它會不會成為爆款（30 天 ≥10 件）。
                </Typography>
                <Stack direction="row" spacing={3} sx={{ mt: 2, flexWrap: 'wrap', rowGap: 1 }}>
                  {[
                    [pct(data.base), '新品會成為爆款'],
                    [pct(data.hit_rev_share), '新品 30 天營收來自爆款'],
                    [`${data.missing_count}/${data.likely_count}`, '潛力款還沒上 Meta'],
                  ].map(([v, k]) => (
                    <Box key={k} sx={{ borderTop: 2, borderColor: 'text.primary', pt: 0.75, minWidth: 96 }}>
                      <Typography sx={{ fontSize: 24, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{v}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {k}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  上架第 7 天賣了幾件 → 成為爆款的機率
                </Typography>
                {/* jsonb does not keep key order — walk the buckets in their real order */}
                {BUCKETS.filter((b) => data.day7[b]).map((b) => {
                  const [n, h] = data.day7[b];
                  const rate = n ? h / n : 0;
                  return (
                    <Box key={b} sx={{ display: 'grid', gridTemplateColumns: '72px 1fr 44px 52px', columnGap: 1, alignItems: 'center', py: 0.4 }}>
                      <Typography variant="body2">{BUCKET_LABEL[b] || b}</Typography>
                      <Box sx={{ height: 16, bgcolor: 'action.hover', borderRadius: 1, overflow: 'hidden' }}>
                        <Box sx={{ width: `${Math.max(rate * 100, 1)}%`, height: '100%', bgcolor: rate >= 0.6 ? HOT : 'text.primary', borderRadius: 1 }} />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {pct(rate)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
                        {h}/{n}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Paper>

          {!data.meta_checked && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              今天沒有讀到 Meta 廣告資料，下面無法分出有沒有上 Meta。
            </Alert>
          )}

          {/* ── likely hits with no ad ── */}
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            潛力款還沒上 Meta · {missing.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            機率 ≥30%，但帳上沒有任何以這個 SKU 開頭的廣告。勾選後一鍵用影片管線 v2 做成 PAUSED 測試廣告，每款一個 ABO 測試組（每天 NT$250）；之後在「商品測試」頁判定：停、加到 500，或在 500 跑滿一週還 {'<'} NT$250/筆 就複製到 CBO。
          </Typography>
          {missing.length ? grid(missing.map((r) => card(r, true))) : (
            <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>
              潛力款都已經在 Meta ✓
            </Typography>
          )}

          <Typography variant="h6" sx={{ fontWeight: 800, mt: 4 }}>
            已經在 Meta · {covered.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            這些潛力款已經有廣告，預算由 CBO 決定，不用動。
          </Typography>
          {grid(covered.map((r) => card(r, false)))}

          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              其他新品 · {rest.length}
            </Typography>
            <Button size="small" onClick={() => setShowRest((v) => !v)}>
              {showRest ? '收起' : '展開'}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            近 21 天上架、目前機率低於 30% 的款。第 7 天還是 0 件的，過去只有約 2% 會中。
          </Typography>
          {showRest && grid(rest.map((r) => card(r, false)))}

          {/* ── search every product ── */}
          <Typography variant="h6" sx={{ fontWeight: 800, mt: 4 }}>
            找商品做影片廣告{catalog.length ? ` · ${catalog.filter((c) => c.ads === 0).length} 款還沒有廣告` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            所有上架中的商品。「已有影片」＝電腦裡已經有這款的 Seedance 影片。勾選還沒有廣告的款，一樣用影片管線 v2 做成 PAUSED 廣告，放進同主題、還沒滿的測試廣告組（都滿了才開新的）。
          </Typography>
          {catalog.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              商品清單會在下次雷達更新（每 3 小時）後出現。
            </Alert>
          ) : (
            <>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setShown(PAGE);
                  }}
                  placeholder="SKU 或商品名，例如 3416、襯衫、寬褲"
                  sx={{ flex: '1 1 260px', minWidth: 0 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Chip
                  label="只看沒有廣告"
                  color={onlyNoAd ? 'primary' : 'default'}
                  variant={onlyNoAd ? 'filled' : 'outlined'}
                  onClick={() => {
                    setOnlyNoAd((v) => !v);
                    setShown(PAGE);
                  }}
                />
                <Chip
                  label="只看已有影片"
                  color={onlyVideo ? 'primary' : 'default'}
                  variant={onlyVideo ? 'filled' : 'outlined'}
                  onClick={() => {
                    setOnlyVideo((v) => !v);
                    setShown(PAGE);
                  }}
                />
                <Chip
                  label="近 30 天上架"
                  color={onlyRecent ? 'primary' : 'default'}
                  variant={onlyRecent ? 'filled' : 'outlined'}
                  onClick={() => {
                    setOnlyRecent((v) => !v);
                    setShown(PAGE);
                  }}
                />
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    排序
                  </Typography>
                  {(
                    [
                      ['new', '最新上架'],
                      ['sold', '30 天賣最多'],
                    ] as const
                  ).map(([k, label]) => (
                    <Chip
                      key={k}
                      label={label}
                      size="small"
                      color={sortBy === k ? 'primary' : 'default'}
                      variant={sortBy === k ? 'filled' : 'outlined'}
                      onClick={() => {
                        setSortBy(k);
                        setShown(PAGE);
                      }}
                    />
                  ))}
                </Box>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {found.length} 款符合
              </Typography>
              {grid(found.slice(0, shown).map(productCard))}
              {found.length > shown && (
                <Button sx={{ mt: 1.5 }} onClick={() => setShown((n) => n + PAGE * 2)}>
                  再顯示 {Math.min(PAGE * 2, found.length - shown)} 款
                </Button>
              )}
            </>
          )}

          {/* ── picking guide ── */}
          <Typography variant="h6" sx={{ fontWeight: 800, mt: 4 }}>
            挑款指南：哪種東西會中
          </Typography>
          <Typography variant="body2" color="text.secondary">
            下次在 Sinsang 挑款前先看。爆款以件數定義，貴的款較難達標，所以一起看每款營收。
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '1fr 1fr' }, gap: 2, mt: 1.5 }}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                品類
              </Typography>
              {bars(data.types, (k) => TYPE_ZH[k] || k)}
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                價格帶
              </Typography>
              {bars(data.bands, (k) => k)}
            </Paper>
          </Box>

          {/* ── clean-up ── */}
          <Typography variant="h6" sx={{ fontWeight: 800, mt: 4 }}>
            下架候選 · {data.dead_count}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 260px' }}>
              上架 30–120 天、還在架上、第一個月一件都沒賣。點選要下架的款，下面按「下架」（官網＋蝦皮）。
            </Typography>
            <Button
              size="small"
              onClick={() => {
                const free = data.dead.filter((d) => !delistSent.has(d.sku || d.sid)).slice(0, 30);
                const all = free.every((d) => delist[d.sid]);
                setDelist(all ? {} : Object.fromEntries(free.map((d) => [d.sid, { sid: d.sid, sku: d.sku, title: shortTitle(d.title) }])));
              }}
            >
              {Object.keys(delist).length ? '全部取消' : '全選（最多 30）'}
            </Button>
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: 1.25, mt: 1.5 }}>
            {data.dead.map((d) => {
              const on = !!delist[d.sid];
              const done = delistSent.has(d.sku || d.sid);
              return (
              <Box
                key={d.sid}
                onClick={done ? undefined : () => toggleDelist(d)}
                sx={{ minWidth: 0, cursor: done ? 'default' : 'pointer', position: 'relative' }}
              >
                {d.img && (
                  <Box
                    component="img"
                    src={d.img}
                    alt=""
                    loading="lazy"
                    sx={{
                      width: '100%',
                      aspectRatio: '3 / 4',
                      objectFit: 'cover',
                      borderRadius: 2,
                      filter: on ? 'none' : 'grayscale(.6)',
                      opacity: done ? 0.4 : on ? 1 : 0.85,
                      outline: on ? `3px solid ${HOT}` : 'none',
                      outlineOffset: -3,
                      display: 'block',
                    }}
                  />
                )}
                {!done && (
                  <Checkbox
                    checked={on}
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleDelist(d)}
                    sx={{ position: 'absolute', right: 2, top: 2, bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1, p: 0.25 }}
                  />
                )}
                <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.35, mt: 0.5 }}>
                  {shortTitle(d.title).slice(0, 18)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  SKU {d.sku || '—'} · {d.age} 天 · 0 件
                </Typography>
                {done && (
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: 'success.main' }}>
                    已送下架
                  </Typography>
                )}
              </Box>
              );
            })}
          </Box>
        </>
      )}

      {/* ── delist bar ── */}
      {picked.size === 0 && Object.keys(delist).length > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            zIndex: 10,
            borderRadius: 6,
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            maxWidth: 'calc(100% - 32px)',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            已選 {Object.keys(delist).length} 款要下架
          </Typography>
          <Button variant="contained" color="error" disableElevation disabled={delisting} onClick={startDelist}>
            {delisting ? '開始中…' : '下架（官網＋蝦皮）'}
          </Button>
          <Button color="inherit" size="small" onClick={() => setDelist({})}>
            清除
          </Button>
        </Paper>
      )}

      {/* ── selection bar ── */}
      {picked.size > 0 && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
            zIndex: 10,
            borderRadius: 6,
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
            maxWidth: 'calc(100% - 32px)',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            已選 {picked.size} 款（{Array.from(picked).join('、')}）＝ {picked.size} 支 Seedance 影片
          </Typography>
          {pickedWithVideo.length > 0 && (
            <Typography variant="caption" sx={{ fontWeight: 700, color: HOT, width: '100%' }}>
              {pickedWithVideo.join('、')} 已經有影片，會再做一支新的
            </Typography>
          )}
          <Button
            variant="contained"
            disableElevation
            startIcon={<MovieCreationOutlinedIcon />}
            disabled={starting}
            onClick={startVideos}
            sx={{ bgcolor: HOT, '&:hover': { bgcolor: '#c93a25' } }}
          >
            {starting ? '開始中…' : '用影片管線 v2 做廣告'}
          </Button>
          <Button color="inherit" size="small" onClick={() => setPicked(new Set())}>
            清除
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default HitRadarPage;
